import { FC, SyntheticEvent, useContext } from 'react'
import { useSelector } from 'react-redux'
import { ScreenContext } from 'react-components'
import BottomSheet from '../../../common/bottom-sheet'
import WishlistSection from '../../../common/wishlist-section'
import { MailPickerIcon } from '../../header-section/searchbar/widget-styles'
import ImageCarousel from '../../../common/image-carousel'
import { useTranslation } from '../../../../hooks/locale'
import { useAppDispatch } from '../../../../redux/hooks'
import { getSelectedListings, getSelectedCount, deselectListing } from '../../../../redux/bulk-inquiry'
import { BULK_INQUIRY } from '../../../../constants/search-result-listing'
import { GeneralEquipmentsEnum } from '../../../../transports/property-details-page-transport'
import { ListingSectionObject } from '../../../../transports/common/listing-section-transport'
import SearchRegionNodeTransport from '../../../../transports/search-region-node-transport'
import { getPropertyClickRedirectUrl } from '../../../region-landing-page/utils'
import { stopPropagation } from '../../../../utils/dom-utils'
import { getCategory } from '../../../../utils/category-utils'
import AnalyticsService from '../../../../services/analytics-service'
import { bulkInquiryPageCategories } from '../../../../services/analytics-service/categories'
import { bulkInquiryPageActions, pdpPageActions } from '../../../../services/analytics-service/actions'
import {
    CompareModalShell,
    ModalContainer,
    Header,
    HeaderTitle,
    CloseButton,
    ChevronDown,
    Body,
    Row,
    CardsBox,
    Column,
    ImageBox,
    ImageCarouselClip,
    HeartOverlay,
    InfoBlock,
    Region,
    Title,
    Section,
    StatLine,
    RatingLine,
    StyledStar,
    RatingValue,
    RatingCount,
    NoRating,
    PriceMain,
    PriceSub,
    PriceStrike,
    Discount,
    InfoRow,
    InfoText,
    CheckMark,
    CrossMark,
    RemoveLink,
    AddMorePanel,
    PlusAvatar,
    StyledPlus,
    AddMoreTitle,
    AddMoreSubtitle,
    BrowseButton,
    SavedText,
    Footer,
    SelectedCount,
    SendButton,
    MobileBody,
    MobileFooter,
    MobileSendButton,
} from './compare-modal-styles'

interface Props {
    isOpen: boolean
    onClose: () => void
    onSendInquiry: () => void
    searchRegionNode: SearchRegionNodeTransport | null
}

const AMENITIES: { key: GeneralEquipmentsEnum; labelKey: string }[] = [
    { key: GeneralEquipmentsEnum.WIFI, labelKey: 'featureWifi' },
    { key: GeneralEquipmentsEnum.TV, labelKey: 'featureTelevision' },
    { key: GeneralEquipmentsEnum.DISH_WASHER, labelKey: 'featureDishwasher' },
    { key: GeneralEquipmentsEnum.WASHING_MACHINE, labelKey: 'featureWashingMachine' },
    { key: GeneralEquipmentsEnum.POOL, labelKey: 'featurePool' },
]

const WISHLIST_ICON_PROPS = {
    containerBorderRadius: '1.456rem',
    containerHeight: '1.5rem',
    containerWidth: '1.5rem',
    containerMargin: '0',
    containerPadding: '0',
    width: '1.5rem',
    height: '1.5rem',
    showBoxShadow: false,
    strokeColor: 'white',
    containerBackgroundColor: 'transparent',
} as const

const STATS_SECTION_MIN_HEIGHT = '5.625rem'
const RATING_SECTION_MIN_HEIGHT = '2.875rem'
const PRICE_SECTION_MIN_HEIGHT = '4.375rem'
const CANCELLATION_SECTION_MIN_HEIGHT = '4.25rem'
const BOTTOM_SHEET_MAX_HEIGHT_RATIO = 0.8
const IMAGE_CAROUSEL_HEIGHT = '8.5rem'
const IMAGE_CAROUSEL_NAV_BUTTON_SIZE = '1.5rem'
const IMAGE_CAROUSEL_NAV_BUTTON_ICON_SIZE = '1rem'

const CompareModal: FC<Props> = ({ isOpen, onClose, onSendInquiry, searchRegionNode }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { isMobile } = useContext(ScreenContext)
    const listings = useSelector(getSelectedListings)
    const count = useSelector(getSelectedCount)

    const remaining = BULK_INQUIRY.MAX_SELECTION - count
    const amenityRows = AMENITIES.filter(amenity =>
        listings.some(listing => listing.generalEquipments && amenity.key in listing.generalEquipments),
    )

    const traumBaseUrl = t('traumBaseUrl')

    const handleClose = () => {
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.COMPARE_MODAL,
            action: bulkInquiryPageActions.COMPARE_MODAL_CLOSED,
        })
        onClose()
    }

    const handleCardClick = (listing: ListingSectionObject) => () => {
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.COMPARE_MODAL,
            action: bulkInquiryPageActions.COMPARE_MODAL_IMAGE_CLICKED,
            value: String(listing.id),
        })

        AnalyticsService.trackEvent({
            event: pdpPageActions.SELECT_ITEM,
            ecommerce: {
                items: [
                    {
                        item_id: listing.id,
                        item_name: listing.title,
                        affiliation: listing.providerId,
                        item_category: getCategory(!!listing.isComboListing),
                        price: listing.price?.notFormattedPrice,
                        currency: listing.price?.currency,
                        quantity: 1,
                    },
                ],
            },
            arrival: searchRegionNode?.arrival ?? undefined,
            departure: searchRegionNode?.departure ?? undefined,
            persons: (searchRegionNode?.adults ?? 0) + (searchRegionNode?.children ?? 0) || undefined,
        })
        const redirectUrl = getPropertyClickRedirectUrl(traumBaseUrl, listing.href)
        window.open(redirectUrl.toString(), '_blank')
    }

    const handleRemove = (listing: ListingSectionObject) => () => {
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.COMPARE_MODAL,
            action: bulkInquiryPageActions.LISTING_DESELECTED_FROM_COMPARE_MODAL,
            value: String(listing.id),
        })
        dispatch(deselectListing(String(listing.id)))
    }

    const handleWishlistClick = (e: SyntheticEvent) => {
        stopPropagation(e)
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.COMPARE_MODAL,
            action: bulkInquiryPageActions.COMPARE_MODAL_WISHLIST_CLICKED,
        })
    }

    const handleCarouselNavClick = (listing: ListingSectionObject) => () => {
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.COMPARE_MODAL,
            action: bulkInquiryPageActions.COMPARE_MODAL_CAROUSEL_INTERACTED,
            value: String(listing.id),
        })
    }

    const handleAddMoreAvatarClick = () => {
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.COMPARE_MODAL,
            action: bulkInquiryPageActions.ADD_MORE_AVATAR_CLICKED,
        })
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.COMPARE_MODAL,
            action: bulkInquiryPageActions.ADD_MORE_INTERACTED,
        })
        onClose()
    }

    const handleBrowseClick = () => {
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.COMPARE_MODAL,
            action: bulkInquiryPageActions.ADD_MORE_BROWSE_CLICKED,
        })
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.COMPARE_MODAL,
            action: bulkInquiryPageActions.ADD_MORE_INTERACTED,
        })
        onClose()
    }

    const handleSendInquiryClick = () => {
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.COMPARE_MODAL,
            action: bulkInquiryPageActions.SEND_INQUIRY_FROM_COMPARE_MODAL_CLICKED,
            value: String(count),
        })
        onSendInquiry()
    }

    const renderColumn = (listing: ListingSectionObject) => {
        const hasRating = Boolean(listing.rating?.average)
        const strike = listing.price?.priceBeforeOffer
        const discount = listing.offer?.discount
        const isFreeCancellation = Boolean(listing.isFreeCancellation)

        return (
            <Column key={listing.id}>
                <ImageBox onClick={handleCardClick(listing)}>
                    <ImageCarouselClip>
                        <ImageCarousel
                            images={listing.images}
                            horizontalCardHeight={IMAGE_CAROUSEL_HEIGHT}
                            imageCarouselMargin="0"
                            navButtonMargin="0"
                            navButtonSize={IMAGE_CAROUSEL_NAV_BUTTON_SIZE}
                            navButtonIconSize={IMAGE_CAROUSEL_NAV_BUTTON_ICON_SIZE}
                            onNavButtonClick={handleCarouselNavClick(listing)}
                        />
                    </ImageCarouselClip>
                    <HeartOverlay onClick={handleWishlistClick}>
                        <WishlistSection listingId={String(listing.id)} {...WISHLIST_ICON_PROPS} />
                    </HeartOverlay>
                </ImageBox>
                <InfoBlock>
                    <Region>{listing.seoRegion?.name}</Region>
                    <Title>{listing.title}</Title>
                </InfoBlock>
                <Section $minHeight={STATS_SECTION_MIN_HEIGHT}>
                    <StatLine>{t('bulkInquiry.bedrooms', { count: listing.stats?.bedrooms || 1 })}</StatLine>
                    <StatLine>{t('bulkInquiry.bathrooms', { count: listing.stats?.bathrooms || 1 })}</StatLine>
                    {listing.stats?.maxPersons && (
                        <StatLine>{t('bulkInquiry.maxGuests', { count: listing.stats.maxPersons })}</StatLine>
                    )}
                </Section>
                <Section $minHeight={RATING_SECTION_MIN_HEIGHT}>
                    <RatingLine>
                        {hasRating ? (
                            <>
                                <StyledStar />
                                <RatingValue>{listing.rating?.average?.toFixed(1)}</RatingValue>
                                <RatingCount>({listing.rating?.count})</RatingCount>
                            </>
                        ) : (
                            <NoRating>{t('bulkInquiry.noRatings')}</NoRating>
                        )}
                    </RatingLine>
                </Section>
                <Section $minHeight={PRICE_SECTION_MIN_HEIGHT}>
                    <PriceMain>{listing.price?.price}</PriceMain>
                    <PriceSub>
                        {strike && <PriceStrike>{strike}</PriceStrike>}
                        {!!discount && <Discount>{t('bulkInquiry.percentOff', { discount })}</Discount>}
                    </PriceSub>
                </Section>
                <Section $minHeight={CANCELLATION_SECTION_MIN_HEIGHT}>
                    <InfoRow>
                        {isFreeCancellation ? <CheckMark $positive /> : <CrossMark />}
                        <InfoText $positive={isFreeCancellation} $muted={!isFreeCancellation}>
                            {isFreeCancellation ? t('bulkInquiry.freeCancellation') : t('bulkInquiry.nonRefundable')}
                        </InfoText>
                    </InfoRow>
                </Section>
                {amenityRows.length > 0 && (
                    <Section>
                        {amenityRows.map(amenity => {
                            const hasAmenity = Boolean(listing.generalEquipments?.[amenity.key])
                            return (
                                <InfoRow key={amenity.key}>
                                    {hasAmenity ? <CheckMark /> : <CrossMark />}
                                    <InfoText $muted={!hasAmenity}>{t(amenity.labelKey)}</InfoText>
                                </InfoRow>
                            )
                        })}
                    </Section>
                )}
                <RemoveLink onClick={handleRemove(listing)}>{t('bulkInquiry.remove')}</RemoveLink>
            </Column>
        )
    }

    const header = (
        <Header>
            <HeaderTitle>{t('bulkInquiry.compareTitle')}</HeaderTitle>
            <CloseButton aria-label={t('bulkInquiry.closeCompare')} onClick={handleClose}>
                <ChevronDown />
            </CloseButton>
        </Header>
    )

    const cardsBox = <CardsBox>{listings.map(listing => renderColumn(listing))}</CardsBox>

    const addMorePanel = remaining > 0 && (
        <AddMorePanel>
            <PlusAvatar aria-label={t('bulkInquiry.browseAccommodations')} onClick={handleAddMoreAvatarClick}>
                <StyledPlus />
            </PlusAvatar>
            <AddMoreTitle>{t('bulkInquiry.addMoreToCompare', { count: remaining })}</AddMoreTitle>
            <AddMoreSubtitle>{t('bulkInquiry.addMoreSubtitle')}</AddMoreSubtitle>
            <BrowseButton onClick={handleBrowseClick}>{t('bulkInquiry.browseAccommodations')}</BrowseButton>
            <SavedText>{t('bulkInquiry.accommodationsSaved', { count })}</SavedText>
        </AddMorePanel>
    )

    if (isMobile) {
        return (
            <BottomSheet
                rootElementSelector="#__next"
                isOpen={isOpen}
                onClose={handleClose}
                enableSwipeToClose={false}
                header={header}
                content={
                    <MobileBody>
                        <Row>
                            {cardsBox}
                            {addMorePanel}
                        </Row>
                    </MobileBody>
                }
                footer={
                    <MobileFooter>
                        <MobileSendButton onClick={handleSendInquiryClick}>
                            {t('bulkInquiry.sendInquiryToAll')}
                        </MobileSendButton>
                    </MobileFooter>
                }
                maxHeightRatio={BOTTOM_SHEET_MAX_HEIGHT_RATIO}
            />
        )
    }

    return (
        <CompareModalShell isOpen={isOpen} onRequestClose={handleClose} preventScroll>
            <ModalContainer>
                {header}
                <Body>
                    <Row>
                        {cardsBox}
                        {addMorePanel}
                    </Row>
                </Body>
                <Footer>
                    <SelectedCount>
                        {t('bulkInquiry.accommodationsSelected', { selected: count, max: BULK_INQUIRY.MAX_SELECTION })}
                    </SelectedCount>
                    <SendButton onClick={handleSendInquiryClick}>
                        <MailPickerIcon aria-label={t('mailIconText')} style={{ position: 'unset' }} />
                        {t('bulkInquiry.sendInquiryToAll')}
                    </SendButton>
                </Footer>
            </ModalContainer>
        </CompareModalShell>
    )
}

export default CompareModal
